import { useCallback, useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { cx, focusRing } from './cx';
import { useEdgeFade } from './useEdgeFade';

export interface TabItem {
  id: string;
  label: ReactNode;
  /** Count shown after the label, for 'Tranches 4'. */
  count?: number;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  /**
   * What this set of tabs is for, announced to a screen reader.
   *
   * A tablist with no accessible name is read as "tab list" and nothing
   * more, which on a page with ten of them is no help at all. Do NOT
   * reach for <nav> instead: a tablist is not a navigation landmark, and
   * wrapping it in one gives a screen reader two conflicting stories
   * about what it is looking at. The name is the fix.
   */
  label?: string;
  /** Rendered for the active tab only, inside the tabpanel. */
  children?: ReactNode;
  className?: string;
}

/**
 * Underline tabs: Instrument Sans 600, a 2px green rule under the active one, on a
 * hairline. Proper tab semantics: arrow keys move between tabs, only the
 * active tab is in the tab order, and the panel is associated with its tab.
 *
 * On a phone a strip of four or five tabs is wider than the screen, and it
 * used to scroll with no sign that it could: Settings read "Team, Invite
 * people, Access requests, Wo" and stopped. The edge that has more tabs
 * behind it now fades out over 12px, and the active tab is scrolled into
 * the strip when it changes.
 */
export function Tabs({ items, value, onChange, label, children, className }: TabsProps) {
  const base = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const fade = useEdgeFade(listRef, [items.length]);

  // Bring the active tab into the strip. Horizontal only: scrollIntoView
  // would also scroll the page to the strip, which on first render is a
  // jump the reader did not ask for.
  useEffect(() => {
    const list = listRef.current;
    const tab = list?.querySelector<HTMLElement>(`#${CSS.escape(`${base}-tab-${value}`)}`);
    if (!list || !tab) return;
    const pad = 16;
    // The strip is the tabs' offset parent (it is `relative`).
    const start = tab.offsetLeft;
    const end = start + tab.offsetWidth;
    if (start - pad < list.scrollLeft) list.scrollLeft = Math.max(0, start - pad);
    else if (end + pad > list.scrollLeft + list.clientWidth) list.scrollLeft = end + pad - list.clientWidth;
  }, [value, base]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const enabled = items.filter((i) => !i.disabled);
      const current = enabled.findIndex((i) => i.id === value);
      let next = -1;
      if (e.key === 'ArrowRight') next = (current + 1) % enabled.length;
      else if (e.key === 'ArrowLeft') next = (current - 1 + enabled.length) % enabled.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = enabled.length - 1;
      if (next < 0) return;
      e.preventDefault();
      onChange(enabled[next].id);
      listRef.current?.querySelector<HTMLElement>(`#${CSS.escape(`${base}-tab-${enabled[next].id}`)}`)?.focus();
    },
    [items, value, onChange, base],
  );

  return (
    <div className={className}>
      <div
        ref={listRef}
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
        style={fade}
        className="relative flex scroll-px-4 gap-4 overflow-x-auto border-b border-line"
      >
        {items.map((item) => {
          const active = item.id === value;
          return (
            <button
              key={item.id}
              id={`${base}-tab-${item.id}`}
              role="tab"
              type="button"
              aria-selected={active}
              // aria-controls must name an element that exists. Only the
              // active panel is ever rendered, and callers that lay their own
              // content out below the tab strip (the demo does) pass no
              // children at all, so no panel exists for any tab. Emitted only
              // when there is genuinely a panel to point at; a dangling
              // reference is a WCAG failure axe rates critical.
              aria-controls={children && active ? `${base}-panel-${item.id}` : undefined}
              tabIndex={active ? 0 : -1}
              disabled={item.disabled}
              onClick={() => onChange(item.id)}
              className={cx(
                'inline-flex min-h-tap shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-0 text-sm font-semibold -mb-px',
                'transition-colors duration-state ease-ct disabled:opacity-50 disabled:cursor-not-allowed',
                focusRing,
                active ? 'border-forest text-ink' : 'border-transparent text-ink-3 hover:text-ink',
              )}
            >
              {item.label}
              {typeof item.count === 'number' ? <span className="record text-2xs text-ink-3">{item.count}</span> : null}
            </button>
          );
        })}
      </div>
      {children ? (
        <div
          role="tabpanel"
          id={`${base}-panel-${value}`}
          aria-labelledby={`${base}-tab-${value}`}
          tabIndex={0}
          className="pt-4 outline-none"
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
